import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Space, Table, Tag, Typography, message, Popconfirm } from 'antd';
import tenantService from '../../../services/tenantService';
import { buildTenantLoginUrl } from '../../../config/appContext';

const statusColor = {
  active: 'green',
  suspended: 'orange',
  pending: 'blue',
};

const TenantList = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const data = await tenantService.listar();
      setTenants(Array.isArray(data) ? data : []);
    } catch {
      message.error('No se pudieron cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSuspend = async (id) => {
    await tenantService.suspender(id);
    message.success('Cliente suspendido');
    load();
  };

  const handleActivate = async (id) => {
    await tenantService.activar(id);
    message.success('Cliente activado');
    load();
  };

  const handleDelete = async (id) => {
    await tenantService.eliminar(id);
    message.success('Cliente eliminado');
    load();
  };

  const columns = [
    { title: 'Empresa', dataIndex: 'company_name', key: 'company_name' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    {
      title: 'Dominio',
      dataIndex: 'domain',
      key: 'domain',
      render: (domain) => domain || '—',
    },
    {
      title: 'URL de login',
      key: 'login_url',
      render: (_, row) => {
        const url = buildTenantLoginUrl(row.slug);
        if (!url) return '—';

        return (
          <Typography.Text copyable={{ text: url }} style={{ fontSize: 12 }}>
            <a href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
          </Typography.Text>
        );
      },
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_, row) => {
        if (!row.plan) return '—';
        const buildings =
          row.plan.max_inmuebles === 1
            ? '1 edificio'
            : `${row.plan.max_inmuebles} edificios`;
        return (
          <div>
            <div>{row.plan.name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {buildings} · {row.plan.max_usuarios} usuarios
            </Typography.Text>
          </div>
        );
      },
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={statusColor[status] || 'default'}>{status}</Tag>,
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, row) => (
        <Space>
          {row.status === 'active' ? (
            <Button size="small" onClick={() => handleSuspend(row.id)}>
              Suspender
            </Button>
          ) : (
            <Button size="small" type="primary" onClick={() => handleActivate(row.id)}>
              Activar
            </Button>
          )}
          <Popconfirm
            title="¿Eliminar cliente y su base de datos?"
            onConfirm={() => handleDelete(row.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button size="small" danger>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Clientes (Tenants)"
      extra={
        <Link to="/tenants/new">
          <Button type="primary">Nuevo cliente</Button>
        </Link>
      }
    >
      <Typography.Paragraph type="secondary">
        Administra empresas registradas en RentAHAB. Cada cliente tiene su propia base de datos aislada.
      </Typography.Paragraph>
      <Table rowKey="id" loading={loading} columns={columns} dataSource={tenants} pagination={{ pageSize: 10 }} />
    </Card>
  );
};

export default TenantList;
